/**
 * ⚠️ HAND-WRITTEN FILE - DO NOT DELETE! ⚠️
 *
 * Uses page — interactive 3D desk scene + categorized list of tools, hardware, and software.
 */

import { createFileRoute } from '@tanstack/react-router';
import {
  Box,
  Container,
  Typography,
  Stack,
  Grid,
  Paper,
  Chip,
  IconButton,
  Button,
  Slider,
  Fade,
  Divider,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LaunchIcon from '@mui/icons-material/Launch';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import EditIcon from '@mui/icons-material/Edit';
import EditOffIcon from '@mui/icons-material/EditOff';
import LabelIcon from '@mui/icons-material/Label';
import LabelOffIcon from '@mui/icons-material/LabelOff';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import ThreeSixtyIcon from '@mui/icons-material/ThreeSixty';
import ComputerIcon from '@mui/icons-material/Computer';
import CodeIcon from '@mui/icons-material/Code';
import TerminalIcon from '@mui/icons-material/Terminal';
import LanguageIcon from '@mui/icons-material/Language';
import DeskIcon from '@mui/icons-material/Desk';
import AppsIcon from '@mui/icons-material/Apps';
import BuildIcon from '@mui/icons-material/Build';
import CloudIcon from '@mui/icons-material/Cloud';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useState, useEffect, useRef, useCallback, lazy } from 'react';
import { usesData, type UsesCategory, type UsesItem } from '@/lib/uses';
import { pageColors, getGradient } from '@/theme/colors';
import { useThemeContext } from '@/contexts/ThemeContext';
import { ThreeSceneShell } from '@/components/ThreeSceneShell';
import { DEFAULT_LAYOUT, ITEM_LABELS, type SceneLayout, type LayoutItem } from '@/components/usesSceneLayout';
import { createPageMeta, createCollectionJsonLd, PAGE_KEYWORDS } from '@/lib/meta';
import { analytics } from '@/lib/analytics';

const UsesSceneLazy = lazy(() =>
  import('@/components/UsesScene').then((m) => ({ default: m.UsesScene })),
);

export const Route = createFileRoute('/uses')({
  component: UsesPage,
  head: () => {
    const base = createPageMeta({
      title: 'Uses - Christian Battaglia',
      description: 'Hardware, software, and tools I use daily (from my MacBook Pro and mechanical keyboard to Cursor, Nix, and infrastructure setup).',
      path: '/uses',
      image: '/og/uses.png',
      imageAlt: 'Uses — Christian Battaglia',
      keywords: PAGE_KEYWORDS.uses,
    });
    return {
      ...base,
      links: [
        ...base.links,
        { rel: 'preload', href: '/models/standingDesk.glb', as: 'fetch', crossOrigin: 'anonymous' },
        { rel: 'preload', href: '/models/electricPiano.glb', as: 'fetch', crossOrigin: 'anonymous' },
        { rel: 'preload', href: '/models/gamingHeadset.glb', as: 'fetch', crossOrigin: 'anonymous' },
        { rel: 'preload', href: '/draco/draco_decoder.wasm', as: 'fetch', crossOrigin: 'anonymous' },
      ],
      scripts: [
        createCollectionJsonLd({
          name: 'Uses — Christian Battaglia',
          description: 'Hardware, software, and tools I use daily.',
          path: '/uses',
          keywords: PAGE_KEYWORDS.uses,
        }),
      ],
    };
  },
});

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  'Computers': <ComputerIcon />,
  'Editor & IDE': <CodeIcon />,
  'Terminal': <TerminalIcon />,
  'Browser': <LanguageIcon />,
  'Desk Setup': <DeskIcon />,
  'Productivity': <AppsIcon />,
  'Development Tools': <BuildIcon />,
  'Infrastructure & Hosting': <CloudIcon />,
  'AI': <AutoAwesomeIcon />,
};

const CATEGORY_COLORS: Record<string, string> = {
  'Computers': '#3b82f6',
  'Editor & IDE': '#8b5cf6',
  'Terminal': '#10b981',
  'Browser': '#f59e0b',
  'Desk Setup': '#ef4444',
  'Productivity': '#ec4899',
  'Development Tools': '#06b6d4',
  'Infrastructure & Hosting': '#f97316',
  'AI': '#a855f7',
};

function DetailPanel({
  category,
  onClose,
}: {
  category: UsesCategory;
  onClose: () => void;
}) {
  const color = CATEGORY_COLORS[category.title] || '#c2410c';

  return (
    <Fade in>
      <Paper
        elevation={8}
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: { xs: '100%', sm: 380 },
          bgcolor: 'background.paper',
          borderLeft: `3px solid ${color}`,
          overflowY: 'auto',
          zIndex: 20,
        }}
      >
        <Box sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ color }}>{CATEGORY_ICONS[category.title]}</Box>
              <Typography variant="h6" fontWeight="bold">
                {category.title}
              </Typography>
            </Stack>
            <IconButton onClick={onClose} size="small" aria-label="Close" sx={{ minWidth: 44, minHeight: 44 }}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Stack spacing={2.5}>
            {category.items.map((item) => (
              <Box key={item.name}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" fontWeight={700}>
                    {item.name}
                  </Typography>
                  {item.url && (
                    <IconButton
                      component="a"
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      onClick={() => analytics.usesItemClicked(category.title, item.name, item.url!)}
                      sx={{ color }}
                    >
                      <LaunchIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Stack>
                {item.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {item.description}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      </Paper>
    </Fade>
  );
}

function ItemCard({ item, accentColor, categoryTitle }: { item: UsesItem; accentColor: string; categoryTitle: string }) {
  return (
    <Paper
      sx={{
        p: 2.5,
        height: '100%',
        transition: 'transform 0.2s, box-shadow 0.2s',
        borderTop: `3px solid ${accentColor}`,
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        },
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
            {item.url ? (
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => analytics.usesItemClicked(categoryTitle, item.name, item.url!)}
                style={{
                  color: 'inherit',
                  textDecoration: 'underline',
                  textDecorationStyle: 'dotted',
                  textUnderlineOffset: '3px',
                }}
              >
                {item.name}
              </a>
            ) : (
              item.name
            )}
          </Typography>
          {item.url && (
            <LaunchIcon sx={{ fontSize: 14, color: 'text.secondary', mt: 0.5, flexShrink: 0 }} />
          )}
        </Stack>
        {item.description && (
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {item.description}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

const LAYOUT_STORAGE_KEY = 'uses-scene-layout-v9';

function loadLayout(): SceneLayout {
  if (typeof window === 'undefined') return DEFAULT_LAYOUT;
  try {
    const raw = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (raw) {
      const saved = JSON.parse(raw) as SceneLayout;
      const defaultKeys = Object.keys(DEFAULT_LAYOUT).sort().join(',');
      const savedKeys = Object.keys(saved).sort().join(',');
      if (defaultKeys !== savedKeys) {
        localStorage.removeItem(LAYOUT_STORAGE_KEY);
        return DEFAULT_LAYOUT;
      }
      return saved;
    }
  } catch { /* ignore corrupt data */ }
  return DEFAULT_LAYOUT;
}

function UsesPage() {
  const { mode } = useThemeContext();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const sceneContainerRef = useRef<HTMLDivElement>(null);

  const [editMode, setEditMode] = useState(false);
  const [hideLabels, setHideLabels] = useState(false);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [layout, setLayout] = useState<SceneLayout>(loadLayout);
  const [transformMode, setTransformMode] = useState<'translate' | 'rotate'>('translate');

  useEffect(() => {
    const handleFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!sceneContainerRef.current) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
      analytics.sceneFullscreenToggled(false);
    } else {
      sceneContainerRef.current.requestFullscreen();
      analytics.sceneFullscreenToggled(true);
    }
  }, []);

  // Persist layout to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layout));
  }, [layout]);

  const handleItemMoved = useCallback((key: string, partial: Partial<LayoutItem>) => {
    setLayout((prev) => ({
      ...prev,
      [key]: { ...prev[key], ...partial },
    }));
  }, []);

  const handleScaleChange = useCallback((_: unknown, value: number | number[]) => {
    if (!selectedItem) return;
    const factor = value as number;
    const def = DEFAULT_LAYOUT[selectedItem];
    setLayout((prev) => ({
      ...prev,
      [selectedItem]: {
        ...prev[selectedItem],
        scale: [def.scale[0] * factor, def.scale[1] * factor, def.scale[2] * factor] as [number, number, number],
      },
    }));
  }, [selectedItem]);

  const handleFieldChange = useCallback((field: 'position' | 'rotation' | 'scale', axis: number, raw: string) => {
    if (!selectedItem) return;
    const value = parseFloat(raw);
    if (Number.isNaN(value)) return;
    setLayout((prev) => {
      const arr = [...prev[selectedItem][field]] as [number, number, number];
      arr[axis] = value;
      const update: Partial<LayoutItem> = { [field]: arr };
      if (field === 'scale') {
        update.scale = [value, value, value];
      }
      return { ...prev, [selectedItem]: { ...prev[selectedItem], ...update } };
    });
  }, [selectedItem]);

  const scaleFactor = selectedItem
    ? layout[selectedItem].scale[0] / DEFAULT_LAYOUT[selectedItem].scale[0]
    : 1;

  const resetItem = useCallback(() => {
    if (!selectedItem) return;
    setLayout((prev) => ({ ...prev, [selectedItem]: DEFAULT_LAYOUT[selectedItem] }));
  }, [selectedItem]);

  const resetAll = useCallback(() => {
    setLayout(DEFAULT_LAYOUT);
    setSelectedItem(null);
  }, []);

  const exportJson = useCallback(() => {
    const blob = new Blob([JSON.stringify(layout, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'uses-scene-layout.json';
    a.click();
    URL.revokeObjectURL(url);
  }, [layout]);

  const toggleEditMode = useCallback(() => {
    setEditMode((prev) => {
      const next = !prev;
      if (prev) setSelectedItem(null);
      analytics.sceneEditToggled(next);
      return next;
    });
  }, []);

  const selectedData = selectedCategory
    ? usesData.find((c) => c.title === selectedCategory) || null
    : null;

  const handleSelect = (category: string) => {
    setSelectedCategory((prev) => (prev === category ? null : category));
    analytics.sceneItemSelected(category);
  };

  return (
    <Box sx={{ minHeight: 'calc(100vh - 70px)' }}>
      {/* 3D Scene — top-level hero */}
      <Box
        ref={sceneContainerRef}
        sx={{
          position: 'relative',
          height: isFullscreen ? '100vh' : { xs: '60vh', md: '75vh' },
          bgcolor: '#12131a',
          clipPath: 'inset(0)',
        }}
      >
        <ThreeSceneShell
          sx={{ height: '100%' }}
          message="Loading 3D scene…"
          fallback={
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.6)', px: 3, textAlign: 'center' }}>
              <Typography variant="body1">
                3D scene requires WebGL. Browse the full list below.
              </Typography>
            </Box>
          }
        >
          <UsesSceneLazy
            onSelect={handleSelect}
            selectedCategory={selectedCategory}
            editMode={editMode}
            layout={layout}
            selectedItem={selectedItem}
            onEditSelect={setSelectedItem}
            onItemMoved={handleItemMoved}
            transformMode={transformMode}
            hideLabels={hideLabels}
          />
        </ThreeSceneShell>

        {/* Top-right toolbar */}
        <Stack
          direction="row"
          spacing={0.75}
          sx={{ position: 'absolute', top: 12, right: 12, zIndex: 10 }}
        >
          <IconButton
            onClick={toggleEditMode}
            sx={{
              color: editMode ? '#22d3ee' : 'rgba(255,255,255,0.7)',
              bgcolor: editMode ? 'rgba(34,211,238,0.15)' : 'rgba(0,0,0,0.4)',
              backdropFilter: 'blur(4px)',
              minWidth: 44,
              minHeight: 44,
              '&:hover': { bgcolor: editMode ? 'rgba(34,211,238,0.25)' : 'rgba(0,0,0,0.6)', color: '#fff' },
            }}
            size="small"
            aria-label={editMode ? 'Exit edit mode' : 'Edit scene layout'}
          >
            {editMode ? <EditOffIcon /> : <EditIcon />}
          </IconButton>
          {editMode && (
            <IconButton
              onClick={() => setHideLabels((prev) => !prev)}
              sx={{
                color: hideLabels ? 'rgba(255,255,255,0.4)' : '#22d3ee',
                bgcolor: hideLabels ? 'rgba(0,0,0,0.4)' : 'rgba(34,211,238,0.15)',
                backdropFilter: 'blur(4px)',
                minWidth: 44,
                minHeight: 44,
                '&:hover': { bgcolor: hideLabels ? 'rgba(0,0,0,0.6)' : 'rgba(34,211,238,0.25)', color: '#fff' },
              }}
              size="small"
              aria-label={hideLabels ? 'Show labels' : 'Hide labels'}
            >
              {hideLabels ? <LabelOffIcon /> : <LabelIcon />}
            </IconButton>
          )}
          <IconButton
            onClick={toggleFullscreen}
            sx={{
              color: 'rgba(255,255,255,0.7)',
              bgcolor: 'rgba(0,0,0,0.4)',
              backdropFilter: 'blur(4px)',
              minWidth: 44,
              minHeight: 44,
              '&:hover': { bgcolor: 'rgba(0,0,0,0.6)', color: '#fff' },
            }}
            size="small"
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Stack>

        {/* Edit panel */}
        {editMode && (
          <Fade in>
            <Paper
              elevation={6}
              sx={{
                position: 'absolute',
                top: 12,
                left: 12,
                zIndex: 20,
                width: 280,
                bgcolor: 'rgba(18,19,26,0.92)',
                backdropFilter: 'blur(12px)',
                color: '#e2e8f0',
                border: '1px solid rgba(34,211,238,0.2)',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Box sx={{ px: 2, py: 1.5, bgcolor: 'rgba(34,211,238,0.08)', borderBottom: '1px solid rgba(34,211,238,0.15)' }}>
                <Typography variant="subtitle2" sx={{ color: '#22d3ee', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  Scene Editor
                </Typography>
              </Box>

              <Box sx={{ p: 2 }}>
                {selectedItem ? (
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                          Selected
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#22d3ee' }}>
                          {ITEM_LABELS[selectedItem] || selectedItem}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.25}>
                        <IconButton
                          size="small"
                          onClick={() => setTransformMode('translate')}
                          title="Move (translate)"
                          sx={{
                            color: transformMode === 'translate' ? '#22d3ee' : 'rgba(255,255,255,0.4)',
                            bgcolor: transformMode === 'translate' ? 'rgba(34,211,238,0.15)' : 'transparent',
                            '&:hover': { bgcolor: 'rgba(34,211,238,0.2)' },
                          }}
                        >
                          <OpenWithIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setTransformMode('rotate')}
                          title="Rotate"
                          sx={{
                            color: transformMode === 'rotate' ? '#22d3ee' : 'rgba(255,255,255,0.4)',
                            bgcolor: transformMode === 'rotate' ? 'rgba(34,211,238,0.15)' : 'transparent',
                            '&:hover': { bgcolor: 'rgba(34,211,238,0.2)' },
                          }}
                        >
                          <ThreeSixtyIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Stack>
                    </Stack>

                    {(['position', 'rotation'] as const).map((field) => (
                      <Box key={field}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                          {field}
                        </Typography>
                        <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }}>
                          {['X', 'Y', 'Z'].map((axis, i) => (
                            <TextField
                              key={axis}
                              label={axis}
                              type="number"
                              size="small"
                              value={parseFloat(layout[selectedItem][field][i].toFixed(3))}
                              onChange={(e) => handleFieldChange(field, i, e.target.value)}
                              slotProps={{ htmlInput: { step: field === 'rotation' ? 0.1 : 0.01 } }}
                              sx={{
                                flex: 1,
                                '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.75rem', color: '#e2e8f0', py: 0.75, px: 1 },
                                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.35)', fontSize: '0.65rem' },
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
                                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(34,211,238,0.4)' },
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#22d3ee' },
                              }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    ))}

                    <Box>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                          Scale
                        </Typography>
                        <TextField
                          type="number"
                          size="small"
                          value={parseFloat(scaleFactor.toFixed(3))}
                          onChange={(e) => {
                            const v = parseFloat(e.target.value);
                            if (!Number.isNaN(v)) handleScaleChange(null, v);
                          }}
                          slotProps={{ htmlInput: { step: 0.01, min: 0.01 } }}
                          sx={{
                            width: 80,
                            '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.75rem', color: '#22d3ee', fontWeight: 600, py: 0.5, px: 1, textAlign: 'right' },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(34,211,238,0.25)' },
                            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(34,211,238,0.4)' },
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#22d3ee' },
                          }}
                        />
                      </Stack>
                      <Slider
                        value={scaleFactor}
                        min={0.01}
                        max={5}
                        step={0.01}
                        onChange={handleScaleChange}
                        sx={{
                          color: '#22d3ee',
                          '& .MuiSlider-thumb': { width: 14, height: 14 },
                          '& .MuiSlider-rail': { bgcolor: 'rgba(255,255,255,0.1)' },
                        }}
                      />
                    </Box>

                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<RestartAltIcon />}
                      onClick={resetItem}
                      sx={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.15)', textTransform: 'none', fontSize: '0.75rem' }}
                    >
                      Reset {ITEM_LABELS[selectedItem]}
                    </Button>
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', py: 1, textAlign: 'center' }}>
                    Click an object to select it
                  </Typography>
                )}
              </Box>

              <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

              <Stack direction="row" spacing={1} sx={{ p: 1.5 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RestartAltIcon />}
                  onClick={resetAll}
                  sx={{ flex: 1, color: 'rgba(255,255,255,0.5)', borderColor: 'rgba(255,255,255,0.12)', textTransform: 'none', fontSize: '0.7rem' }}
                >
                  Reset All
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadIcon />}
                  onClick={exportJson}
                  sx={{ flex: 1, color: '#22d3ee', borderColor: 'rgba(34,211,238,0.3)', textTransform: 'none', fontSize: '0.7rem' }}
                >
                  Export
                </Button>
              </Stack>
            </Paper>
          </Fade>
        )}

        {/* Instruction hint */}
        {!selectedCategory && !editMode && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: 'rgba(0,0,0,0.6)',
              color: 'rgba(255,255,255,0.7)',
              px: 2,
              py: 0.75,
              borderRadius: 2,
              fontSize: '0.8rem',
              pointerEvents: 'none',
              backdropFilter: 'blur(4px)',
            }}
          >
            Click objects to explore · Drag to orbit · Scroll to zoom
          </Box>
        )}
        {editMode && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: 'rgba(34,211,238,0.1)',
              color: '#22d3ee',
              px: 2,
              py: 0.75,
              borderRadius: 2,
              fontSize: '0.8rem',
              pointerEvents: 'none',
              backdropFilter: 'blur(4px)',
              border: '1px solid rgba(34,211,238,0.2)',
            }}
          >
            Click to select · Drag arrows to move · Scroll to zoom · Saved automatically
          </Box>
        )}

        {/* Detail panel overlay */}
        {selectedData && <DetailPanel category={selectedData} onClose={() => setSelectedCategory(null)} />}
      </Box>

      {/* Info section (previously the hero) */}
      <Box
        className="py-16"
        sx={{
          background: getGradient(pageColors.uses, mode),
          color: 'white',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 400, opacity: 0.8, letterSpacing: '0.5px' }}
                  >
                    what I use
                  </Typography>
                  <Typography variant="h3" component="h1" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                    /uses
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, opacity: 0.95 }}>
                  The hardware, software, and tools behind the work. Click around the desk above
                  or scroll down for the full list.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
                  <Chip
                    label={`${usesData.reduce((sum, c) => sum + c.items.length, 0)} items`}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    label={`${usesData.length} categories`}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      color: 'white',
                    }}
                  />
                  <Chip
                    component="a"
                    href="https://uses.tech"
                    target="_blank"
                    rel="noopener noreferrer"
                    label="uses.tech ↗"
                    clickable
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.1)',
                      color: 'white',
                      textDecoration: 'none',
                    }}
                  />
                </Stack>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography variant="body1" sx={{ fontSize: '1.05rem', lineHeight: 1.8, opacity: 0.9 }}>
                Inspired by Wes Bos's{' '}
                <a
                  href="https://uses.tech"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit', textDecoration: 'underline' }}
                >
                  uses.tech
                </a>{' '}
                movement — a place where developers share the tools of their trade. The interactive
                3D view above shows my desk setup. Click any object or floating category to explore the details.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Traditional List View */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Stack spacing={6}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Everything I Use
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600 }}>
              The complete list, organized by category. Every link goes to the product or service directly.
            </Typography>
          </Box>

          {usesData.map((category) => {
            const color = CATEGORY_COLORS[category.title] || '#c2410c';

            return (
              <Box key={category.title} id={category.title.toLowerCase().replace(/\s+/g, '-')}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                  <Box sx={{ color, display: 'flex' }}>
                    {CATEGORY_ICONS[category.title]}
                  </Box>
                  <Typography variant="h5" fontWeight="bold">
                    {category.title}
                  </Typography>
                  <Chip
                    label={category.items.length}
                    size="small"
                    sx={{ bgcolor: `${color}20`, color, fontWeight: 700 }}
                  />
                </Stack>

                <Grid container spacing={2}>
                  {category.items.map((item) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.name}>
                      <ItemCard item={item} accentColor={color} categoryTitle={category.title} />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            );
          })}
        </Stack>
      </Container>
    </Box>
  );
}
